import { useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionHeading from "@/components/base/SectionHeading";
import { useSeo } from "@/hooks/useSeo";
import { updateLogs, broadcastSchedule, announcements } from "@/mocks/updates";

export default function UpdatesPage() {
  const [expanded, setExpanded] = useState<string | null>(updateLogs[0]?.id ?? null);
  const [visible, setVisible] = useState(6);

  useSeo({
    title: "善良的小姨子-更新日志与播出信息",
    description:
      "善良的小姨子更新日志与播出信息：最新剧集更新时间、播出时间表及相关公告通知，帮助追剧用户第一时间掌握更新动态。",
    keywords: "善良的小姨子更新时间,善良的小姨子更新日志,善良的小姨子播出时间,追剧更新",
  });

  const shown = updateLogs.slice(0, visible);
  const hasMore = visible < updateLogs.length;

  return (
    <SiteLayout>
      <section className="w-full px-4 md:px-8 pt-6 md:pt-8">
        <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-5">
          <Link to="/" className="hover:text-primary-600 cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700">更新日志</span>
        </nav>

        <SectionHeading
          eyebrow="播出动态"
          title="更新日志与播出信息"
          desc="按时间倒序展示剧集更新记录，点击任意条目可展开查看更新内容简述。"
        />

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 md:gap-8">
          <div>
            {updateLogs.length === 0 ? (
              <p className="text-sm text-foreground-500 py-10 text-center rounded-lg bg-background-100">
                暂无最新更新信息
              </p>
            ) : (
              <div className="space-y-3">
                {shown.map((log) => {
                  const isOpen = expanded === log.id;
                  return (
                    <div
                      key={log.id}
                      className="rounded-lg bg-background-50 border border-background-200 overflow-hidden"
                    >
                      <button
                        type="button"
                        onClick={() => setExpanded(isOpen ? null : log.id)}
                        className="w-full flex items-center gap-4 px-4 md:px-5 py-4 text-left cursor-pointer hover:bg-background-100 transition-colors"
                      >
                        <span className="w-14 h-14 shrink-0 rounded-lg bg-primary-50 flex flex-col items-center justify-center">
                          <span className="text-[10px] text-primary-500 leading-none">第</span>
                          <span className="font-heading text-lg font-bold text-primary-700 leading-tight">
                            {log.episode}
                          </span>
                          <span className="text-[10px] text-primary-500 leading-none">集</span>
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="flex items-center gap-2 flex-wrap">
                            <span className="font-heading text-sm md:text-base font-bold text-foreground-950 truncate">
                              {log.title}
                            </span>
                            {log.tag && (
                              <span className="px-1.5 py-0.5 rounded bg-accent-100 text-accent-800 text-[10px] font-medium">
                                {log.tag}
                              </span>
                            )}
                          </span>
                          <span className="block text-xs text-foreground-400 mt-1">{log.date}</span>
                        </span>
                        <i
                          className={`${
                            isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"
                          } text-xl text-foreground-400 shrink-0`}
                        ></i>
                      </button>
                      {isOpen && (
                        <div className="px-4 md:px-5 pb-4 -mt-1 animate-fade-in">
                          <p className="text-sm text-foreground-700 leading-relaxed border-t border-background-200 pt-3">
                            {log.summary}
                          </p>
                          <Link
                            to={`/play/${log.episode}`}
                            className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
                          >
                            <i className="ri-play-circle-line"></i> 前往观看第{log.episode}集
                          </Link>
                        </div>
                      )}
                    </div>
                  );
                })}

                {hasMore && (
                  <div className="flex justify-center pt-2">
                    <button
                      type="button"
                      onClick={() => setVisible((v) => Math.min(v + 4, updateLogs.length))}
                      className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-background-50 border border-background-300 text-foreground-800 text-sm font-medium hover:bg-background-200 hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line"></i> 加载更多更新记录
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <aside className="space-y-6">
            <div className="rounded-lg bg-background-50 border border-background-200 p-4 md:p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2 mb-4">
                <i className="ri-calendar-schedule-line text-primary-500"></i> 播出时间表
              </h3>
              <div className="space-y-3">
                {broadcastSchedule.map((row) => (
                  <div
                    key={row.date}
                    className="flex items-center justify-between gap-3 rounded-md bg-background-100 px-3 py-2.5"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground-900">
                        {row.weekday} · {row.episodes}
                      </p>
                      <p className="text-xs text-foreground-400 mt-0.5">
                        {row.date} {row.time}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 px-2 py-0.5 rounded text-[10px] font-medium ${
                        row.status === "已排播"
                          ? "bg-secondary-100 text-secondary-900"
                          : "bg-accent-100 text-accent-800"
                      }`}
                    >
                      {row.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg bg-primary-50 border border-primary-100 p-4 md:p-5">
              <h3 className="font-heading text-base font-bold text-primary-900 flex items-center gap-2 mb-4">
                <i className="ri-megaphone-line"></i> 公告通知
              </h3>
              <div className="space-y-4">
                {announcements.map((an) => (
                  <div key={an.id}>
                    <p className="text-sm font-medium text-primary-900">{an.title}</p>
                    <p className="text-xs text-primary-700 mt-1 leading-relaxed">{an.content}</p>
                    <p className="text-[11px] text-primary-500 mt-1">{an.date}</p>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}